/**
 * Central de Ajuda / Tutoriais V2 (React, tema dark igual ao admin V2).
 * Migração de templates/tutorials.html. Mantém TUDO do V1: 44 tutoriais (mesmo
 * conteúdo), 8 categorias, busca profunda com sinônimos + snippet destacado,
 * filtro da sidebar, drawer mobile, vídeos do YouTube. Modernizado: home com
 * grid de categorias, tema escuro, transições suaves.
 *
 * Standalone — roda em tutorials_shell.html com output.css. Bundle:
 * static/v2/tutorials.js. Flag-gated no backend (beta).
 */
import { StrictMode, useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { installAnimationGuard } from '../../shared/animationGuard';
import { categories, tutorials } from './data';
import { deepSearch } from './search';
import { TopBar } from './components/TopBar';
import { HomeGrid } from './components/HomeGrid';
import { Sidebar } from './components/Sidebar';
import { ArticleView } from './components/ArticleView';
import { SearchResults } from './components/SearchResults';
import { MobileDrawer } from './components/MobileDrawer';

installAnimationGuard();

declare const __BUILD_TIME__: string;

function TutorialsApp() {
  const [query, setQuery] = useState('');
  // null = home. O hash da URL manda: `/tutorials#cardapio-produtos` abre o
  // artigo direto — é como o botão "Ver tutorial completo" da ajuda contextual
  // (modal do base.html) chega aqui. Hash desconhecido cai na home.
  const [activeKey, setActiveKey] = useState<string | null>(() => {
    const k = decodeURIComponent((window.location.hash || '').replace(/^#/, ''));
    return k && tutorials[k] ? k : null;
  });
  const [mobileNav, setMobileNav] = useState(false);

  const searching = query.trim().length >= 2;
  const hits = useMemo(() => (searching ? deepSearch(query) : []), [query, searching]);

  const openTutorial = useCallback((key: string) => {
    if (!tutorials[key]) return;
    setActiveKey(key);
    setQuery('');
    setMobileNav(false);
    // replaceState (e não `location.hash =`) pra não empilhar uma entrada no
    // histórico a cada clique da sidebar — o "voltar" continua saindo da página.
    window.history.replaceState(null, '', '#' + key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goHome = useCallback(() => {
    setActiveKey(null);
    setQuery('');
    setMobileNav(false);
    window.history.replaceState(null, '', window.location.pathname);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // A aba do Help Center costuma ficar aberta: se o lojista clicar na ajuda de
  // OUTRA tela, o navegador reusa a aba e só troca o hash — sem isto a página
  // continuaria mostrando o artigo antigo.
  useEffect(() => {
    function onHash() {
      const k = decodeURIComponent((window.location.hash || '').replace(/^#/, ''));
      if (k && tutorials[k]) {
        setActiveKey(k);
        setQuery('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (!k) {
        setActiveKey(null);
      }
    }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // ESC fecha o drawer mobile.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileNav(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onHome = activeKey === null && !searching;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-700">
      <TopBar
        query={query}
        onQuery={setQuery}
        onClear={() => setQuery('')}
        onHome={goHome}
        onToggleNav={() => setMobileNav((v) => !v)}
        showNavToggle={!onHome}
      />

      <div className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {searching ? (
          <SearchResults query={query} hits={hits} onOpen={openTutorial} />
        ) : onHome ? (
          <HomeGrid onOpen={openTutorial} />
        ) : (
          <div className="flex gap-6">
            {/* Sidebar desktop */}
            <aside className="hidden md:block w-72 shrink-0">
              <div className="sticky top-24">
                <Sidebar activeKey={activeKey} onOpen={openTutorial} />
              </div>
            </aside>
            {/* Conteúdo */}
            <main className="flex-1 min-w-0">
              {activeKey ? <ArticleView tutorialKey={activeKey} /> : null}
            </main>
          </div>
        )}
      </div>

      {/* Drawer mobile (sidebar) */}
      <MobileDrawer
        open={mobileNav}
        activeKey={activeKey}
        onClose={() => setMobileNav(false)}
        onOpen={openTutorial}
      />

      <footer className="bg-white border-t border-gray-200 py-6 text-center text-xs text-slate-400">
        ALIV Hub — Central de Ajuda · {categories.length} categorias · {Object.keys(tutorials).length} tutoriais
      </footer>
    </div>
  );
}

function boot() {
  console.log('[V2 Tutorials] BUNDLE BUILD:', __BUILD_TIME__);
  const rootEl = document.querySelector<HTMLElement>('#tutorials-root');
  if (!rootEl) {
    console.warn('[V2 Tutorials] #tutorials-root não encontrado');
    return;
  }
  createRoot(rootEl).render(
    <StrictMode>
      <TutorialsApp />
    </StrictMode>,
  );
}

boot();
